import {
  Accordion,
  Button,
  Stack,
  Alert,
  Text,
  useWizardPage,
  Skeleton,
  DEFAULT_TEXT_HEIGHT,
  Spacer,
  Icon,
  FlexSpacer,
  List,
  Title,
  Paragraph,
  Wizard,
  Link
} from '@efast_public/fjd-component-library';

import './DataModel.css';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { WeiterleitungsWizardState } from '../../../../modals/ReceiverSettingsWizard/Wizard.types';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import DatamodelUpload from './DataModelUpload';
import { ClientDestinationsModel } from '../../../../api/hooks/useDestinations';
import { useMissingDataModels } from './useMissingDataModels';
import {
  FJD_DOCS_EINRICHTUNGSASSISTANT,
  FJD_DOCS_SETTINGS_FITCONNECT
} from '../../../../shared/utils/Links';

function OutcomeCheckMark({ success }: { success: boolean }) {
  return success ? (
    <Icon name="close-filled" variant="warning" />
  ) : (
    <Icon name="checkmark-filled" variant="success" />
  );
}

function DataModel({ isSettings }: { isSettings?: boolean }) {
  const { t } = useTranslation();
  const [openDataModelUploadModal, setOpenDataModelUploadModal] =
    useState<boolean>(false);
  const { wizardState, setWizardState } = useWizardPage({
    isFormValid: true,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`
  });

  const wizardOrgUnits = (
    wizardState.state as WeiterleitungsWizardState | undefined
  )?.orgUnits;
  const { data: fetchedOrgUnits, isLoading: isLoadingOrgUnits } =
    useQuery<ClientDestinationsModel>({
      queryKey: [BehApiRoutes.destinations()],
      queryFn: async () => {
        const response = await behClient.get<ClientDestinationsModel>(
          BehApiRoutes.destinations()
        );
        return response.data;
      },
      enabled: !wizardOrgUnits
    });
  const resolvedOrgUnits = wizardOrgUnits ?? fetchedOrgUnits;

  // Use the shared hook for missing data models information
  const { dataModels, isLoadingDatamodels: isLoadingMissingModels } =
    useMissingDataModels();

  const filteredDataModels = useMemo(() => {
    if (!resolvedOrgUnits) return dataModels;
    const knownIdentifiers = new Set(
      resolvedOrgUnits.organizationalUnitDtos.flatMap(
        (orgUnit: ClientDestinationsModel['organizationalUnitDtos'][number]) =>
          orgUnit.destinationDtos.flatMap((destination) =>
            destination.publicServiceTypeDto.map(
              (service) => service.identifier
            )
          )
      )
    );
    return Object.fromEntries(
      Object.entries(dataModels).filter(([identifier]) =>
        knownIdentifiers.has(identifier)
      )
    );
  }, [dataModels, resolvedOrgUnits]);

  const hasMissingFilteredModels = useMemo(
    () =>
      Object.values(filteredDataModels)
        .flat()
        .some((model) => !model.isDataModelProvided),
    [filteredDataModels]
  );

  const orgUnitsWithDataModels = React.useMemo(() => {
    if (!resolvedOrgUnits) {
      return [];
    }

    return resolvedOrgUnits.organizationalUnitDtos.map(
      (orgUnit: ClientDestinationsModel['organizationalUnitDtos'][number]) => {
        const publicServices = orgUnit.destinationDtos.flatMap(
          (destination) => destination.publicServiceTypeDto
        );
        const serviceIdentifiers = publicServices.map(
          (service) => service.identifier
        );
        const orgDataModels = Object.entries(filteredDataModels).filter(
          ([identifier]) => serviceIdentifiers.includes(identifier)
        );

        return {
          orgUnit,
          publicServices,
          dataModels: orgDataModels,
          hasMissingModels:
            publicServices.length === 0 ||
            publicServices.some((service) =>
              (filteredDataModels[service.identifier] ?? []).some(
                (model) => !model.isDataModelProvided
              )
            )
        };
      }
    );
  }, [filteredDataModels, resolvedOrgUnits]);

  useEffect(() => {
    if (filteredDataModels && Object.keys(filteredDataModels).length > 0) {
      const identifiers = Object.keys(filteredDataModels);
      setWizardState((prevState) => ({
        ...prevState,
        state: {
          ...(prevState.state as object),
          identifiers
        } as WeiterleitungsWizardState
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDataModels]);

  return (
    <Wizard.Page>
      <Stack spacing="m">
        <Title level={2} visualLevel={4}>
          {t('configAssistent.datamodels.headline')}
        </Title>
        <Paragraph>
          {t('configAssistent.datamodels.descriptionText')}
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link
                  href={
                    isSettings
                      ? FJD_DOCS_SETTINGS_FITCONNECT
                      : FJD_DOCS_EINRICHTUNGSASSISTANT
                  }
                  target="_blank"
                >
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        <Title level={2} visualLevel={6}>
          {t('Zustellpunkte')}
        </Title>
        {(isLoadingMissingModels || isLoadingOrgUnits) &&
          ['1', '2', '3', '4'].map((key) => (
            <Skeleton key={key} height={DEFAULT_TEXT_HEIGHT} />
          ))}
        {orgUnitsWithDataModels && (
          <Accordion.Group>
            {orgUnitsWithDataModels.map(
              ({
                orgUnit,
                publicServices,
                hasMissingModels
              }: (typeof orgUnitsWithDataModels)[number]) => {
                return (
                  <Accordion
                    key={orgUnit.ouId}
                    before={<OutcomeCheckMark success={hasMissingModels} />}
                    label={orgUnit.name}
                  >
                    <Stack spacing="m">
                      <FlexSpacer />
                      <Text>
                        {t('configAssistent.datamodels.synchronizedServices')}
                      </Text>
                      {publicServices.length > 0 ? (
                        <Stack spacing="m">
                          {publicServices.map((service, serviceIndex) => {
                            const hasMissingModelsForIdentifier = (
                              filteredDataModels[service.identifier] ?? []
                            ).some((model) => !model.isDataModelProvided);
                            return (
                              <Accordion
                                key={`${orgUnit.ouId}-${service.identifier}-${serviceIndex}`}
                                id={`${orgUnit.ouId}-${service.identifier}-${serviceIndex}`}
                                label={service.identifier}
                                before={
                                  <OutcomeCheckMark
                                    success={hasMissingModelsForIdentifier}
                                  />
                                }
                              >
                                <Spacer />
                                <Stack direction="row">
                                  <Spacer size="m" />
                                  <List type={'none'}>
                                    <>
                                      <Text>
                                        {t(
                                          'configAssistent.datamodels.sychronizedModels'
                                        )}
                                      </Text>
                                      <Spacer size="m" />
                                      {service.submissionSchemaDtos.length ===
                                        0 && (
                                        <Text variant="warning">
                                          ⚠️ Keine Datenmodelle gefunden. Bitte
                                          überprüfen sie ihre Zustellpunkte im
                                          FIT-Connect Self-Service-Portal.
                                        </Text>
                                      )}
                                      {service.submissionSchemaDtos.map(
                                        (schema, schemaIndex) => {
                                          const hasMissingModel = (
                                            filteredDataModels[
                                              service.identifier
                                            ] ?? []
                                          )
                                            .filter(
                                              (model) =>
                                                model.datamodelVersion ===
                                                schema.schemaUri
                                            )
                                            .some(
                                              (model) =>
                                                !model.isDataModelProvided
                                            );
                                          return (
                                            <List.ListItem
                                              key={`${orgUnit.ouId}-${service.identifier}-${schema.schemaUri}-${schemaIndex}`}
                                              id={`${orgUnit.ouId}-${service.identifier}-${schemaIndex}`}
                                              {...(hasMissingModel
                                                ? {
                                                    icon: (
                                                      <Icon
                                                        name="close-filled"
                                                        variant="warning"
                                                      />
                                                    )
                                                  }
                                                : {})}
                                            >
                                              <p className="icon-text-container">
                                                <OutcomeCheckMark
                                                  success={hasMissingModel}
                                                />
                                                <Text>{schema.schemaUri}</Text>
                                              </p>
                                            </List.ListItem>
                                          );
                                        }
                                      )}
                                    </>
                                  </List>
                                </Stack>
                              </Accordion>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Text variant="warning">
                          ⚠️ Keine Datenmodelle gefunden. Bitte überprüfen sie
                          ihre Zustellpunkte im FIT-Connect Self-Service-Portal.
                        </Text>
                      )}
                    </Stack>
                  </Accordion>
                );
              }
            )}
          </Accordion.Group>
        )}
        {!isLoadingMissingModels && hasMissingFilteredModels && (
          <Alert
            id="dataModelUploadAlert"
            data-testid="dataModelUploadAlert"
            variant="warning"
            message={
              <Stack direction="column">
                <Text variant="warning" size="s">
                  {t('receiverSettingsWizard.datamodel.manualUploadPrompt')}
                </Text>
                <Spacer size="m" />
                <Button
                  id="uploadButtonAlert"
                  data-testid="uploadButtonAlert"
                  beforeIcon={'upload'}
                  onClick={() => setOpenDataModelUploadModal(true)}
                  size="sm"
                  key={'openDataModelUploadModal'}
                >
                  Datenmodelle hochladen
                </Button>
              </Stack>
            }
          ></Alert>
        )}
        {!hasMissingFilteredModels && !isLoadingMissingModels && (
          <Alert
            variant="success"
            message={t('receiverSettingsWizard.datamodel.isFormValid')}
            key={'dataModelUploadAlertSuccess'}
          ></Alert>
        )}
      </Stack>
      {openDataModelUploadModal && (
        <DatamodelUpload
          isOpen={openDataModelUploadModal}
          setOpenModal={setOpenDataModelUploadModal}
        />
      )}
    </Wizard.Page>
  );
}

export default DataModel;
